import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import RequestFundingSummary, {
    fundingGate,
    resolveRailFacts,
    RequestWalletFigure,
} from './RequestFundingSummary';
import './pendingClientRequests.css';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Review modal for cancel_booking requests. The outcome is decided against the
// LIVE booking at approval time:
//   pending booking            → archived (+ anything charged refunded)
//   confirmed, >24h to start   → archived + net-charged refunded
//   confirmed, <24h to start   → Last Minute Cancelled (fee kept, rest refunded)
export default function CancelRequestReview({ request, onClose, onDone, readOnlyOutcome = null }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const rejectReasonRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`/api/client-change-requests/${request.id}`);
                setDetail(res.data?.request || null);
            } catch (e) {
                console.error('Error loading request detail:', e);
                setError('Failed to load the request details.');
            } finally {
                setLoading(false);
            }
        })();
    }, [request.id]);

    useEffect(() => {
        if (rejectMode && rejectReasonRef.current) {
            rejectReasonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current.focus();
        }
    }, [rejectMode]);

    const approve = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res = await axios.post(`/api/client-change-requests/${request.id}/approve`, {});
            onDone('approved', res.data?.message, res.data?.result_id);
        } catch (err) {
            console.error('Error approving cancellation:', err);
            setError(err.response?.data?.error || 'Failed to approve the cancellation.');
        } finally {
            setSubmitting(false);
        }
    };

    const reject = async () => {
        if (!rejectReason.trim()) {
            setError('A rejection reason is required.');
            rejectReasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current?.focus();
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`/api/client-change-requests/${request.id}/reject`, { reason: rejectReason.trim() });
            onDone('rejected');
        } catch (err) {
            console.error('Error rejecting cancellation:', err);
            setError(err.response?.data?.error || 'Failed to reject the request.');
        } finally {
            setSubmitting(false);
        }
    };

    const p = (detail || request).payload || {};
    const live = detail?.live || null;
    const requestStatus = detail?.status;
    const requestGone = !!(requestStatus && requestStatus !== 'pending');

    // FUNDING TRUTH comes from the server (F4). This screen used to recompute
    // its own refund and fee-shortfall figures from live.net_charged — the one
    // admin surface still deriving money in the UI, and therefore the one that
    // could drift from what approval actually did. The qualitative OUTCOME
    // (archive vs Last Minute Cancelled) is still decided here because it is a
    // statement about BOOKING STATE, not about money; every dirham figure now
    // comes from RequestFundingSummary / the server payload.
    //
    // enforceBlockers is FALSE: the cancel_booking approve branch does not
    // check funding blockers, and it must not — a cancellation refunds money
    // and cannot be held hostage to a funding problem with the original charge.
    const gate = fundingGate(request, detail, { enforceBlockers: false });

    // P3 — WHAT IS ACTUALLY REFUNDABLE (direct card rail, 2026-07-18).
    //
    // This screen's "Charged from wallet" row read `live.net_charged`, which sums
    // wallet booking_charge/booking_refund rows ONLY. A booking paid on the direct
    // card rail has none of those — its money is in booking_payment_receipts — so
    // the row printed AED 0.00 over a fully-paid booking and a reviewer approving
    // a cancellation had every reason to believe there was nothing to give back.
    //
    // The server now sends the settled figure (getBookingSettledTotal: wallet net
    // + receipts − refunds). We prefer it and SPLIT it, because "refunded to the
    // wallet" and "refunded to the card" are different promises to the client.
    //
    // The honest-degradation rule, unchanged from the rest of this wave: if the
    // server did not send the settled figure and we have positive evidence the
    // booking was card-paid, we do NOT print the wallet-only number as if it were
    // the whole truth — we say the card side is not shown rather than imply zero.
    //
    // FIELD NAMES (reconciled 2026-07-18). This block was written against a
    // payload shape the server never shipped: it read `settled_total`/`card_paid`
    // and treated `net_charged` as the wallet-only figure. The server
    // (routes/clientChangeRequests.js) actually sends:
    //     net_charged        = the FULL settled total (wallet + card)
    //     net_charged_wallet = the wallet-only net
    //     net_charged_card   = the card-only paid figure
    // Reading the old names made hasSettled permanently false and
    // cardFigureMissing permanently true for every card-paid booking, so the
    // panel printed "the wallet ledger alone shows AED 210.00, which does NOT
    // include the card payment" over a booking whose wallet ledger shows AED 0.00
    // and whose 210 IS the card payment — the statement inverted, inviting a
    // second refund out of the wallet on top of the card refund. The legacy
    // payload fallbacks are kept so a stored payload from before the rail still
    // renders.
    const walletNet = Number(live?.net_charged_wallet ?? p.net_charged_wallet ?? p.net_charged ?? 0);
    const settledRaw = Number(live?.net_charged ?? p.settled_total);
    const cardRaw = Number(live?.net_charged_card ?? p.card_paid);
    const hasSettled = Number.isFinite(settledRaw);
    const cardSettled = Number.isFinite(cardRaw)
        ? cardRaw
        : (hasSettled ? Math.max(0, settledRaw - (Number.isFinite(walletNet) ? walletNet : 0)) : null);
    const settledTotal = hasSettled ? settledRaw : (Number.isFinite(walletNet) ? walletNet : 0);
    const cancelRail = resolveRailFacts(request, detail);
    // Card evidence with no server figure to back it = the one case we must not
    // report as a plain number.
    const cardFigureMissing = !hasSettled && cancelRail.cardPaid;

    // Blocked states (approve would 409) + the live outcome preview
    let blockedNote = null;
    let outcome = null; // { lastMinute, pendingBooking }
    if (requestGone) {
        blockedNote = requestStatus === 'cancelled'
            ? 'The client has cancelled this request.'
            : `This request has already been ${requestStatus}.`;
    } else if (live) {
        const status = String(live.status_name || '').trim().toLowerCase();
        if (live.is_deleted || status === 'deleted') blockedNote = 'This booking is already deleted.';
        else if (status !== 'confirmed' && status !== 'pending') blockedNote = `This booking is now ${live.status_name} and can no longer be cancelled this way.`;
        else if (live.invoice_id != null) blockedNote = 'This booking has been invoiced — handle the cancellation manually.';
        else if (!live.future_ok) blockedNote = 'This booking has already started or passed.';
        // The 24h window is judged at REQUEST time (an on-time request is never
        // penalised by a slow review) — the flag was stored on submission.
        else if (status === 'pending' || p.is_last_minute_at_request !== true) {
            outcome = { lastMinute: false, pendingBooking: status === 'pending' };
        } else {
            outcome = { lastMinute: true };
        }
    }

    const safeClose = () => { if (!submitting) onClose(); };

    return (
        <div className="pcr-modal-overlay" onClick={safeClose}>
            <div className="pcr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pcr-modal-header">
                    <div>
                        <h3>Review Cancellation</h3>
                        <div className="pcr-modal-sub">
                            {request.client_display_name || request.client_company_name} · {p.property_label || `#${request.target_id}`}
                            {` · booking #${request.target_id}`} · requested by {request.requested_by_first_name} {request.requested_by_last_name}
                        </div>
                    </div>
                    <button className="pcr-modal-close" onClick={safeClose} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pcr-modal-body">
                    {loading ? (
                        <div className="pcr-empty">Loading request…</div>
                    ) : (
                        <>
                            {blockedNote && (
                                <div className="pcr-error" style={{ marginBottom: 12 }}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} /> {blockedNote}
                                    {!requestGone && ' Approving will fail — reject the request instead.'}
                                </div>
                            )}

                            <div className="pcr-section">
                                <div className="pcr-section-title">Booking</div>
                                <div className="pcr-fields">
                                    <div className="pcr-field">
                                        <span>Scheduled</span>
                                        {(p.booking_start || '').slice(0, 16) || '—'}
                                    </div>
                                    <div className="pcr-field">
                                        <span>Service</span>
                                        {p.type_label || '—'}
                                    </div>
                                    <div className="pcr-field">
                                        <span>Status</span>
                                        {live?.status_name || p.booking_status || '—'}
                                    </div>
                                    <div className="pcr-field">
                                        <span>Booking total</span>
                                        {money(p.current_total)}
                                    </div>
                                    {/* P3 — the refundable figure, split by rail. */}
                                    <div className="pcr-field">
                                        <span>Paid for this booking</span>
                                        {cardFigureMissing ? 'Not shown — see below' : money(settledTotal)}
                                    </div>
                                    {!cardFigureMissing && Number(cardSettled) > 0.005 && (
                                        <>
                                            <div className="pcr-field">
                                                <span>· from wallet</span>
                                                {money(walletNet)}
                                            </div>
                                            <div className="pcr-field">
                                                <span>· by card</span>
                                                {money(cardSettled)}
                                            </div>
                                        </>
                                    )}
                                    {cardFigureMissing && (
                                        <div className="pcr-field pcr-field--wide">
                                            <span>Card payment</span>
                                            This booking was paid by card, and this server did not send the
                                            settled total — so how much is refundable is not shown here rather
                                            than guessed. The wallet ledger alone shows {money(walletNet)},
                                            which does NOT include the card payment. Check the booking&apos;s
                                            payments before approving.
                                        </div>
                                    )}
                                    {detail?.wallet && (
                                        <div className="pcr-field pcr-field--wide">
                                            <span>Client wallet</span>
                                            {/* Was a bare "… avail." — which, now that
                                                available_balance is unclamped, could print a
                                                naked negative with nothing explaining it. */}
                                            <RequestWalletFigure wallet={detail.wallet} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {p.reason && (
                                <div className="pcr-section">
                                    <div className="pcr-section-title">Client&apos;s reason</div>
                                    <div className="pcr-field pcr-field--wide">{p.reason}</div>
                                </div>
                            )}

                            {/* THE SERVER'S FUNDING CONTRACT — the same component and the
                                same computeRequestFunding payload the create/edit reviews
                                render, statement verbatim. This screen states no charge or
                                refund figure of its own. */}
                            <RequestFundingSummary readOnly={!!readOnlyOutcome}
                                request={request}
                                detail={detail}
                                cost={p.current_total}
                                deltaLabel="Booking total"
                                enforceBlockers={false}
                            />

                            {/* OUTCOME = booking state, not money. The dirham consequences
                                are stated once, above, by the funding summary. */}
                            {outcome && (
                                <div className={outcome.lastMinute ? 'pcr-hint' : 'pcr-banner'} style={{ marginTop: 8 }}>
                                    {outcome.lastMinute ? (
                                        <>
                                            <strong>Requested less than 24 hours before the visit</strong> — approving marks the
                                            booking <strong>Last Minute Cancelled</strong> and the fee of{' '}
                                            {money(p.penalty_fee_incl_vat)} applies. The booking reprices to that fee; whatever
                                            the client has already paid above it returns to their wallet, and any shortfall is
                                            charged from it (left unpaid if the balance is short).
                                        </>
                                    ) : outcome.pendingBooking ? (
                                        <>
                                            The booking is still <strong>Pending</strong> — approving archives it
                                            {/* P3: "nothing was charged" must be judged on the SETTLED
                                                total. Judged on the wallet net alone it was asserted over
                                                every card-paid booking, telling the reviewer there was
                                                nothing to refund when the client had paid in full. When
                                                the card figure is missing we make no claim either way. */}
                                            {cardFigureMissing
                                                ? <> and refunds whatever was paid for it — see the note above before approving.</>
                                                : settledTotal > 0.005
                                                    ? <> and refunds the {money(settledTotal)} paid for it.</>
                                                    : <>; nothing was paid, so there is nothing to refund.</>}
                                        </>
                                    ) : (
                                        <>
                                            Approving <strong>archives the booking</strong> and refunds what was paid
                                            for it.
                                        </>
                                    )}
                                </div>
                            )}

                            {/* F5: never let a reviewer approve blind. */}
                            {gate.blocked && (
                                <div className="pcr-error" style={{ marginTop: 8 }}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} /> {gate.reason}
                                </div>
                            )}

                            {rejectMode && (
                                <div className="pcr-section pcr-reason-block pcr-reason-block--reject">
                                    <div className="pcr-section-title">Rejection reason (visible to client)</div>
                                    <textarea ref={rejectReasonRef} className="pcr-reject-reason" value={rejectReason} maxLength={500} placeholder="Explain why this cancellation is being rejected…" onChange={(e) => setRejectReason(e.target.value)} />
                                </div>
                            )}

                            {!!error && <div className="pcr-error">{error}</div>}
                        </>
                    )}
                </div>

                {/* Read-only history view (2026-08-06): rejected/
                    cancelled/approved rows open for inspection — outcome
                    instead of action buttons. */}
                {readOnlyOutcome ? (
                    <div className="pcr-modal-actions" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{readOnlyOutcome.statusLabel}</strong>
                            {readOnlyOutcome.reviewerName ? <> by <strong>{readOnlyOutcome.reviewerName}</strong></> : null}
                            {readOnlyOutcome.reviewedAt ? <> · {readOnlyOutcome.reviewedAt}</> : null}
                            {readOnlyOutcome.reason ? <div><strong>Reason:</strong> {readOnlyOutcome.reason}</div> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {readOnlyOutcome.onOpenEntity ? (
                                <button className="pcr-btn pcr-btn--primary" onClick={readOnlyOutcome.onOpenEntity}>
                                    {readOnlyOutcome.entityLabel}
                                </button>
                            ) : null}
                            <button className="pcr-btn pcr-btn--ghost" onClick={onClose}>Close</button>
                        </div>
                    </div>
                ) : (
                <div className="pcr-modal-actions">
                    {rejectMode ? (
                        <>
                            <button className="pcr-btn pcr-btn--ghost" onClick={() => { setRejectMode(false); setError(''); }} disabled={submitting}>Back</button>
                            <button className="pcr-btn pcr-btn--danger" onClick={reject} disabled={submitting}>{submitting ? 'Rejecting…' : 'Confirm Reject'}</button>
                        </>
                    ) : (
                        <>
                            <button className="pcr-btn pcr-btn--ghost pcr-btn--danger-text" onClick={() => { setRejectMode(true); setError(''); }} disabled={submitting || loading || requestGone}>Reject</button>
                            <button
                                className="pcr-btn pcr-btn--danger"
                                onClick={approve}
                                disabled={submitting || loading || !!blockedNote || gate.blocked}
                                title={gate.blocked ? gate.reason : undefined}
                            >
                                {submitting ? 'Cancelling…' : 'Approve Cancellation'}
                            </button>
                        </>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}
